import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { LogEntry } from '@/types';

interface ScrollAnchor {
    logId: string;
    offset: number;
}

interface LogStore {
    // Data
    logs: Map<string, LogEntry>;
    logIds: string[];

    // UI State
    expandedLogIds: Set<string>;
    scrollAnchor: ScrollAnchor | null;
    newLogsCount: number; // Count of new logs when scrolled down
    scopeId: number; // Increments on reset to prevent stale merges

    // Fetch State
    oldestCursor: string | null;
    newestCursor: string | null;

    // Actions
    mergeNewLogs: (logs: LogEntry[]) => void;
    prependOldLogs: (logs: LogEntry[]) => void;
    toggleExpanded: (logId: string) => void;
    setScrollAnchor: (anchor: ScrollAnchor | null) => void;
    clearOldLogs: () => void;
    setNewLogsCount: (count: number) => void;
    resetNewLogsCount: () => void;
    reset: () => void;

    // Helpers
    isExpanded: (logId: string) => boolean;
}

const MAX_LOGS = 2000;

const getCursorBounds = (logs: Map<string, LogEntry>, logIds: string[]) => ({
    newestCursor: logIds[0] ? (logs.get(logIds[0])?.cursor ?? null) : null,
    oldestCursor: logIds[logIds.length - 1] ? (logs.get(logIds[logIds.length - 1])?.cursor ?? null) : null
});

export const useLogStore = create<LogStore>()(
    devtools(
        (set, get) => ({
            // Initial state
            logs: new Map(),
            logIds: [],
            expandedLogIds: new Set(),
            scrollAnchor: null,
            newLogsCount: 0,
            scopeId: 0,
            oldestCursor: null,
            newestCursor: null,

            // Actions
            mergeNewLogs: (newLogs: LogEntry[]) =>
                set((state) => {
                    if (newLogs.length === 0) return state;

                    const logs = new Map(state.logs);
                    const logIds = [...state.logIds];
                    const expandedLogIds = new Set(state.expandedLogIds);
                    const addedIds: string[] = [];

                    // Add new logs that don't already exist
                    for (const log of newLogs) {
                        const logId = log.event_id;
                        if (!logs.has(logId)) {
                            addedIds.push(logId);
                        }
                        logs.set(logId, log);
                    }

                    // Prepend new logs to the beginning (newest first)
                    if (addedIds.length > 0) {
                        logIds.unshift(...addedIds);

                        // Clear old logs if we exceed the limit
                        if (logIds.length > MAX_LOGS) {
                            const toRemove = logIds.slice(MAX_LOGS);
                            toRemove.forEach((id) => {
                                logs.delete(id);
                                expandedLogIds.delete(id);
                            });
                            logIds.splice(MAX_LOGS);
                        }

                        const cursorBounds = getCursorBounds(logs, logIds);

                        return {
                            logs,
                            logIds,
                            expandedLogIds,
                            ...cursorBounds
                        };
                    }

                    return { logs };
                }),

            prependOldLogs: (oldLogs: LogEntry[]) =>
                set((state) => {
                    if (oldLogs.length === 0) return state;

                    const logs = new Map(state.logs);
                    const logIds = [...state.logIds];
                    const expandedLogIds = new Set(state.expandedLogIds);
                    const addedIds: string[] = [];

                    // Add old logs that don't already exist
                    for (const log of oldLogs) {
                        const logId = log.event_id;
                        if (!logs.has(logId)) {
                            addedIds.push(logId);
                        }
                        logs.set(logId, log);
                    }

                    // Append old logs to the end
                    if (addedIds.length > 0) {
                        logIds.push(...addedIds);

                        // Clear newer logs if we exceed the limit
                        if (logIds.length > MAX_LOGS) {
                            const toRemove = logIds.slice(0, logIds.length - MAX_LOGS);
                            toRemove.forEach((id) => {
                                logs.delete(id);
                                expandedLogIds.delete(id);
                            });
                            logIds.splice(0, logIds.length - MAX_LOGS);
                        }

                        const cursorBounds = getCursorBounds(logs, logIds);

                        return {
                            logs,
                            logIds,
                            expandedLogIds,
                            ...cursorBounds
                        };
                    }

                    return { logs };
                }),

            toggleExpanded: (logId: string) =>
                set((state) => {
                    const expandedLogIds = new Set(state.expandedLogIds);
                    if (expandedLogIds.has(logId)) {
                        expandedLogIds.delete(logId);
                    } else {
                        expandedLogIds.add(logId);
                    }
                    return { expandedLogIds };
                }),

            setScrollAnchor: (anchor: ScrollAnchor | null) => set({ scrollAnchor: anchor }),

            clearOldLogs: () =>
                set((state) => {
                    if (state.logIds.length <= MAX_LOGS) return state;

                    const logs = new Map(state.logs);
                    const logIds = [...state.logIds];
                    const expandedLogIds = new Set(state.expandedLogIds);

                    const toRemove = logIds.slice(MAX_LOGS);
                    toRemove.forEach((id) => {
                        logs.delete(id);
                        expandedLogIds.delete(id);
                    });

                    return {
                        logs,
                        logIds: logIds.slice(0, MAX_LOGS),
                        expandedLogIds,
                        ...getCursorBounds(logs, logIds.slice(0, MAX_LOGS))
                    };
                }),

            setNewLogsCount: (count: number) => set({ newLogsCount: count }),

            resetNewLogsCount: () => set({ newLogsCount: 0 }),

            reset: () =>
                set((state) => ({
                    logs: new Map(),
                    logIds: [],
                    expandedLogIds: new Set(),
                    scrollAnchor: null,
                    newLogsCount: 0,
                    scopeId: state.scopeId + 1,
                    oldestCursor: null,
                    newestCursor: null
                })),

            // Helpers
            isExpanded: (logId: string) => get().expandedLogIds.has(logId)
        }),
        {
            name: 'log-store'
        }
    )
);
